import React from "react";
import SwiperCore, { A11y, Mousewheel, SwiperOptions } from "swiper";
import "./Swiper.css";

SwiperCore.use([A11y, Mousewheel]);

type SwiperEventHandler = (swiper: SwiperCore) => void;

const MOUSE_DRAG_THRESHOLD = 4;
const MOMENTUM_MIN_SPEED = 0.15;
const MOMENTUM_MAX_SPEED = 2.5;
const MOMENTUM_DECELERATION = 0.006;
const MOMENTUM_MAX_DURATION_MS = 450;
const MOMENTUM_IDLE_MS = 90;

interface SwiperProps
  extends Omit<React.HTMLAttributes<HTMLDivElement>, "onResize"> {
  a11y?: SwiperOptions["a11y"];
  breakpoints?: SwiperOptions["breakpoints"];
  cssMode?: SwiperOptions["cssMode"];
  freeMode?: SwiperOptions["freeMode"];
  freeModeMomentum?: SwiperOptions["freeModeMomentum"];
  freeModeMomentumBounce?: SwiperOptions["freeModeMomentumBounce"];
  freeModeMomentumRatio?: SwiperOptions["freeModeMomentumRatio"];
  grabCursor?: SwiperOptions["grabCursor"];
  initialSlide?: number;
  mousewheel?: SwiperOptions["mousewheel"];
  onReachEnd?: SwiperEventHandler;
  onResize?: SwiperEventHandler;
  onSlideChange?: SwiperEventHandler;
  onSwiper?: SwiperEventHandler;
  slidesPerView?: SwiperOptions["slidesPerView"];
  spaceBetween?: SwiperOptions["spaceBetween"];
  speed?: SwiperOptions["speed"];
  watchOverflow?: boolean;
}

interface SwiperCallbacks {
  onReachEnd?: SwiperEventHandler;
  onResize?: SwiperEventHandler;
  onSlideChange?: SwiperEventHandler;
  onSwiper?: SwiperEventHandler;
}

/**
 * Adaptador mínimo para o Swiper Core.
 *
 * O componente React distribuído pelo Swiper 6 mantém uma instância destruída
 * entre as duas execuções de efeitos do React 18 StrictMode. Criar a instância
 * dentro do layout effect garante que cada montagem receba uma instância nova.
 */
export const Swiper = ({
  a11y,
  breakpoints,
  children,
  className = "",
  cssMode = false,
  freeMode = false,
  freeModeMomentum = true,
  freeModeMomentumBounce = true,
  freeModeMomentumRatio = 1,
  grabCursor = true,
  initialSlide = 0,
  mousewheel = {
    forceToAxis: true,
    releaseOnEdges: true,
  },
  onReachEnd,
  onResize,
  onSlideChange,
  onSwiper,
  slidesPerView = 1,
  spaceBetween = 0,
  speed = 300,
  watchOverflow = false,
  ...domProps
}: SwiperProps) => {
  const containerRef = React.useRef<HTMLDivElement>(null);
  const wrapperRef = React.useRef<HTMLDivElement>(null);
  const swiperRef = React.useRef<SwiperCore>();
  const callbacksRef = React.useRef<SwiperCallbacks>({});
  const initialOptionsRef = React.useRef<SwiperOptions>();

  callbacksRef.current = {
    onReachEnd,
    onResize,
    onSlideChange,
    onSwiper,
  };

  if (!initialOptionsRef.current) {
    initialOptionsRef.current = {
      a11y,
      breakpoints,
      cssMode,
      freeMode,
      freeModeMomentum,
      freeModeMomentumBounce,
      freeModeMomentumRatio,
      grabCursor,
      initialSlide,
      mousewheel,
      slidesPerView,
      spaceBetween,
      speed,
      watchOverflow,
    };
  }

  React.useLayoutEffect(() => {
    const container = containerRef.current;
    if (!container) return undefined;

    const swiper = new SwiperCore(container, {
      ...initialOptionsRef.current,
      on: {
        reachEnd: (instance) => callbacksRef.current.onReachEnd?.(instance),
        resize: (instance) => callbacksRef.current.onResize?.(instance),
        slideChange: (instance) =>
          callbacksRef.current.onSlideChange?.(instance),
      },
    });

    swiperRef.current = swiper;
    callbacksRef.current.onSwiper?.(swiper);

    return () => {
      if (!swiper.destroyed) swiper.destroy(true, false);
      if (swiperRef.current === swiper) swiperRef.current = undefined;
    };
  }, []);

  React.useLayoutEffect(() => {
    const swiper = swiperRef.current;
    if (swiper && !swiper.destroyed) swiper.update();
  }, [children]);

  React.useEffect(() => {
    const container = containerRef.current;
    const wrapper = wrapperRef.current;
    if (!cssMode || !container || !wrapper) return undefined;

    // No cssMode, a rolagem nativa não acompanha o mouse pressionado.
    let isDragging = false;
    let hasMoved = false;
    let shouldSuppressClick = false;
    let startX = 0;
    let startScrollLeft = 0;
    let activePointerId: number | null = null;
    let releaseClickTimer: number | undefined;
    let momentumFrame: number | undefined;
    let lastMoveTime = 0;
    let lastScrollLeft = 0;
    let scrollVelocity = 0;

    const stopMomentum = () => {
      if (momentumFrame !== undefined) {
        window.cancelAnimationFrame(momentumFrame);
        momentumFrame = undefined;
      }
    };

    const startMomentum = () => {
      if (
        performance.now() - lastMoveTime > MOMENTUM_IDLE_MS ||
        Math.abs(scrollVelocity) < MOMENTUM_MIN_SPEED ||
        window.matchMedia?.("(prefers-reduced-motion: reduce)").matches
      ) {
        return;
      }

      const maxScrollLeft = wrapper.scrollWidth - wrapper.clientWidth;
      if (maxScrollLeft <= 0) return;

      const velocity = Math.max(
        -MOMENTUM_MAX_SPEED,
        Math.min(scrollVelocity, MOMENTUM_MAX_SPEED)
      );
      const duration = Math.min(
        Math.abs(velocity) / MOMENTUM_DECELERATION,
        MOMENTUM_MAX_DURATION_MS
      );
      const start = wrapper.scrollLeft;
      const distance = Math.max(
        -wrapper.clientWidth * 0.5,
        Math.min((velocity * duration) / 2, wrapper.clientWidth * 0.5)
      );
      const target = Math.max(0, Math.min(start + distance, maxScrollLeft));
      if (target === start) return;

      const travelDuration = Math.min(
        duration,
        (2 * Math.abs(target - start)) / Math.abs(velocity)
      );
      let startTime: number | undefined;

      const animate = (time: number) => {
        if (startTime === undefined) startTime = time;

        const progress = Math.min((time - startTime) / travelDuration, 1);
        wrapper.scrollLeft =
          start + (target - start) * (1 - Math.pow(1 - progress, 2));

        if (progress < 1) {
          momentumFrame = window.requestAnimationFrame(animate);
        } else {
          momentumFrame = undefined;
        }
      };

      momentumFrame = window.requestAnimationFrame(animate);
    };

    const stopDragging = (withMomentum: boolean) => {
      if (!isDragging) return;

      isDragging = false;
      shouldSuppressClick = hasMoved;
      container.classList.remove("swiper--dragging");
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerEnd);
      window.removeEventListener("pointercancel", handlePointerEnd);

      if (withMomentum && hasMoved) startMomentum();

      window.clearTimeout(releaseClickTimer);
      releaseClickTimer = window.setTimeout(() => {
        shouldSuppressClick = false;
      }, 0);
    };

    const moveDragging = (event: MouseEvent | PointerEvent) => {
      if (!isDragging) return;

      const distance = event.clientX - startX;
      if (!hasMoved && Math.abs(distance) < MOUSE_DRAG_THRESHOLD) return;

      hasMoved = true;
      wrapper.scrollLeft = startScrollLeft - distance;
      const now = performance.now();
      const elapsed = now - lastMoveTime;
      scrollVelocity =
        elapsed > 0 && elapsed <= MOMENTUM_IDLE_MS
          ? (wrapper.scrollLeft - lastScrollLeft) / elapsed
          : 0;
      lastMoveTime = now;
      lastScrollLeft = wrapper.scrollLeft;
      event.preventDefault();
    };

    const startDragging = (clientX: number) => {
      stopMomentum();
      window.clearTimeout(releaseClickTimer);
      shouldSuppressClick = false;
      isDragging = true;
      hasMoved = false;
      startX = clientX;
      startScrollLeft = Number.isFinite(wrapper.scrollLeft)
        ? wrapper.scrollLeft
        : 0;
      lastMoveTime = performance.now();
      lastScrollLeft = startScrollLeft;
      scrollVelocity = 0;
      container.classList.add("swiper--dragging");
    };

    const handleMouseMove = (event: MouseEvent) => moveDragging(event);
    const handleMouseUp = () => stopDragging(true);

    const handleMouseDown = (event: MouseEvent) => {
      if (event.button !== 0) return;

      startDragging(event.clientX);
      window.addEventListener("mousemove", handleMouseMove);
      window.addEventListener("mouseup", handleMouseUp);
    };

    const handlePointerMove = (event: PointerEvent) => {
      if (event.pointerId !== activePointerId) return;

      if (event.buttons === 0) {
        handlePointerEnd(event);
        return;
      }

      moveDragging(event);
      if (hasMoved && !wrapper.hasPointerCapture(event.pointerId)) {
        wrapper.setPointerCapture(event.pointerId);
      }
    };

    const handlePointerEnd = (event: PointerEvent) => {
      if (event.pointerId !== activePointerId) return;

      if (wrapper.hasPointerCapture(event.pointerId)) {
        wrapper.releasePointerCapture(event.pointerId);
      }
      activePointerId = null;
      stopDragging(event.type !== "pointercancel");
    };

    const handlePointerDown = (event: PointerEvent) => {
      if (event.pointerType === "touch" || event.button !== 0) return;

      activePointerId = event.pointerId;
      startDragging(event.clientX);
      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerEnd);
      window.addEventListener("pointercancel", handlePointerEnd);
    };

    const handleClick = (event: MouseEvent) => {
      if (!shouldSuppressClick) return;

      shouldSuppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    };

    const preventNativeDrag = (event: DragEvent) => {
      event.preventDefault();
    };

    if (window.PointerEvent) {
      wrapper.addEventListener("pointerdown", handlePointerDown);
    } else {
      wrapper.addEventListener("mousedown", handleMouseDown);
    }
    wrapper.addEventListener("click", handleClick, true);
    wrapper.addEventListener("dragstart", preventNativeDrag);
    wrapper.addEventListener("wheel", stopMomentum, { passive: true });
    wrapper.addEventListener("touchstart", stopMomentum, { passive: true });

    return () => {
      window.clearTimeout(releaseClickTimer);
      stopMomentum();
      wrapper.removeEventListener("pointerdown", handlePointerDown);
      wrapper.removeEventListener("mousedown", handleMouseDown);
      wrapper.removeEventListener("click", handleClick, true);
      wrapper.removeEventListener("dragstart", preventNativeDrag);
      wrapper.removeEventListener("wheel", stopMomentum);
      wrapper.removeEventListener("touchstart", stopMomentum);
      window.removeEventListener("mousemove", handleMouseMove);
      window.removeEventListener("mouseup", handleMouseUp);
      window.removeEventListener("pointermove", handlePointerMove);
      window.removeEventListener("pointerup", handlePointerEnd);
      window.removeEventListener("pointercancel", handlePointerEnd);
      container.classList.remove("swiper--dragging");
    };
  }, [cssMode]);

  React.useEffect(() => {
    const container = containerRef.current;
    const wrapper = wrapperRef.current;
    const swiper = swiperRef.current;
    if (cssMode || !container || !wrapper || !swiper) return undefined;

    let shouldSuppressClick = false;
    let releaseClickTimer: number | undefined;

    const handleSliderMove = () => {
      if (
        Math.abs(swiper.touches.currentX - swiper.touches.startX) <
        MOUSE_DRAG_THRESHOLD
      ) {
        return;
      }

      shouldSuppressClick = true;
      container.classList.add("swiper--dragging");
    };

    const handleTouchEnd = () => {
      container.classList.remove("swiper--dragging");
      window.clearTimeout(releaseClickTimer);
      releaseClickTimer = window.setTimeout(() => {
        shouldSuppressClick = false;
      }, 0);
    };

    const handleClick = (event: MouseEvent) => {
      if (!shouldSuppressClick) return;

      shouldSuppressClick = false;
      event.preventDefault();
      event.stopPropagation();
    };

    swiper.on("sliderMove", handleSliderMove);
    swiper.on("touchEnd", handleTouchEnd);
    wrapper.addEventListener("click", handleClick, true);

    return () => {
      window.clearTimeout(releaseClickTimer);
      swiper.off("sliderMove", handleSliderMove);
      swiper.off("touchEnd", handleTouchEnd);
      wrapper.removeEventListener("click", handleClick, true);
      container.classList.remove("swiper--dragging");
    };
  }, [cssMode]);

  return (
    <div
      ref={containerRef}
      className={`swiper swiper-container${
        cssMode ? " swiper--mouse-drag" : ""
      } ${className}`.trim()}
      {...domProps}
    >
      <div ref={wrapperRef} className="swiper-wrapper">
        {children}
      </div>
    </div>
  );
};

export const SwiperSlide = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLDivElement>
>(({ children, className = "", ...props }, ref) => (
  <div
    ref={ref}
    className={`swiper-slide ${className}`.trim()}
    {...props}
  >
    {children}
  </div>
));

SwiperSlide.displayName = "SwiperSlide";
